// ============================================
// PAGE - Encuesta
// ============================================

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Spinner } from '@almacenes/ui';
import { useAuthStore, useEncuesta, useSubmitEncuesta, useLogout } from '@almacenes/core';
import logo from '@/assets/logos/logo.svg';

export const EncuestaPage = () => {
  const { encuestaId } = useParams<{ encuestaId: string }>();
  const navigate = useNavigate();
  const { session } = useAuthStore();
  const { data: encuesta, isLoading } = useEncuesta(encuestaId);
  const { mutate: submitEncuesta, isPending: isSubmitting } = useSubmitEncuesta();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  
  // Estado para las respuestas: preguntaId -> opcionId (number), texto (string) o array de opciones (number[])
  const [respuestas, setRespuestas] = useState<Record<number, number | string | number[]>>({});

  const handleSelectOption = (preguntaId: number, opcionId: number) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: opcionId,
    }));
  };

  const handleToggleCheckbox = (preguntaId: number, opcionId: number) => {
    setRespuestas(prev => {
      const currentValue = prev[preguntaId];
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      
      // Si ya está seleccionado, lo quitamos; si no, lo agregamos
      const newArray = currentArray.includes(opcionId)
        ? currentArray.filter(id => id !== opcionId)
        : [...currentArray, opcionId];
      
      return {
        ...prev,
        [preguntaId]: newArray,
      };
    });
  };

  const handleTextInput = (preguntaId: number, texto: string) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: texto,
    }));
  };

  const handleSubmit = async () => {
    if (!encuesta || !encuestaId) return;

    // Validar que todas las preguntas OBLIGATORIAS estén respondidas
    const preguntasObligatorias = encuesta.preguntas.filter(p => p.esObligatoria);
    const preguntasObligatoriasRespondidas = preguntasObligatorias.filter(p => {
      const respuesta = respuestas[p.preguntaId];
      // Verificar que exista la respuesta y no esté vacía
      if (respuesta === undefined) return false;
      if (typeof respuesta === 'string' && respuesta === '') return false;
      if (Array.isArray(respuesta) && respuesta.length === 0) return false;
      return true;
    });

    if (preguntasObligatoriasRespondidas.length < preguntasObligatorias.length) {
      const faltantes = preguntasObligatorias
        .filter(p => respuestas[p.preguntaId] === undefined || respuestas[p.preguntaId] === '')
        .map((p) => {
          const orden = encuesta.preguntas.findIndex(pr => pr.preguntaId === p.preguntaId) + 1;
          return `${orden}. ${p.pregunta}`;
        });
      
      alert(
        `Por favor responde todas las preguntas obligatorias antes de continuar:\n\n${faltantes.join('\n')}`
      );
      return;
    }

    // Construir el array de respuestas con el formato requerido
    const respuestasArray = Object.entries(respuestas).map(([preguntaId, valor]) => {
      const pregunta = encuesta.preguntas.find(p => p.preguntaId === Number(preguntaId));
      
      let respuestaTexto = '';
      
      // Si es un array (checkbox con múltiples opciones)
      if (Array.isArray(valor)) {
        const textos = valor.map(opcionId => {
          const opcion = pregunta?.opcionesRespuesta?.find(o => o.id === opcionId);
          return opcion?.valor || '';
        }).filter(texto => texto !== '');
        respuestaTexto = textos.map(t => `• ${t}`).join('\n');
      }
      // Si es un número, es una opción de comboBox (radio button)
      else if (typeof valor === 'number') {
        const opcion = pregunta?.opcionesRespuesta?.find(o => o.id === valor);
        respuestaTexto = opcion?.valor || '';
      } 
      // Si es string, es una respuesta de texto
      else {
        respuestaTexto = valor;
      }
      
      return {
        encuestaPreguntaId: Number(preguntaId),
        respuesta: respuestaTexto,
      };
    });

    submitEncuesta(
      {
        encuestaId: Number(encuestaId),
        personaContesta: session?.user.nombre || 'Usuario',
        respuestas: respuestasArray,
      },
      {
        onSuccess: async () => {
          console.log('[EncuestaPage] ✅ Encuesta enviada exitosamente');
          
          // Pequeña pausa para asegurar que React Query persista la actualización
          await new Promise(resolve => setTimeout(resolve, 100));
          
          console.log('[EncuestaPage] Navegando al dashboard...');
          navigate('/dashboard', { replace: true });
        },
        onError: (error) => {
          console.error('[EncuestaPage] ❌ Error al enviar encuesta:', error);
          alert('Error al enviar la encuesta. Por favor intenta nuevamente.');
        },
      }
    );
  };

  const handleOmitir = () => {
    // Solo permitir omitir si no es obligatoria
    if (encuesta && !encuesta.obligatoria) {
      navigate('/dashboard');
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F3F4F9' }}>
        <Spinner />
      </div>
    );
  }

  if (!encuesta) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F3F4F9' }}>
        <p className="text-gray-600">No se pudo cargar la encuesta</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F9' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <img 
          src={logo} 
          alt="Almacenes Digitales" 
          className="h-9"
        />
        <Button
          variant="danger"
          onClick={handleLogout}
          isLoading={isLoggingOut}
        >
          Cerrar sesión
        </Button>
      </div>

      {/* Contenido */}
      <div className="flex-1 flex items-center justify-center px-8 py-8">
        <div className="max-w-3xl w-full bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-4" style={{ color: '#11194F' }}>
              📋 {encuesta.nombre}
            </h1>
            {encuesta.obligatoria && (
              <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-white mb-4" style={{ backgroundColor: '#FF6B35' }}>
                Obligatoria
              </span>
            )}
            <p className="text-gray-700 text-base leading-relaxed bg-blue-50 p-4 rounded-lg border-l-4" style={{ borderColor: '#43BFFF' }}>
              {encuesta.instrucciones}
            </p>
          </div>

          {/* Preguntas */}
          <div className="space-y-6 mb-8">
            {encuesta.preguntas
              .sort((a, b) => a.orden - b.orden)
              .map((pregunta, index) => (
                <div key={pregunta.preguntaId} className="p-6 border-2 border-gray-200 rounded-xl bg-gray-50">
                  <h3 className="font-semibold text-lg mb-4" style={{ color: '#11194F' }}>
                    {index + 1}. {pregunta.pregunta}
                    {pregunta.esObligatoria && (
                      <span className="text-red-500 ml-1" title="Pregunta obligatoria">*</span>
                    )}
                  </h3>

                  {pregunta.renderType === 'comboBox' && pregunta.opcionesRespuesta && (
                    <div className="space-y-3">
                      {pregunta.opcionesRespuesta.map((opcion) => (
                        <label
                          key={opcion.id}
                          className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            respuestas[pregunta.preguntaId] === opcion.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`pregunta-${pregunta.preguntaId}`}
                            value={opcion.id}
                            checked={respuestas[pregunta.preguntaId] === opcion.id}
                            onChange={() => handleSelectOption(pregunta.preguntaId, opcion.id)}
                            className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700 flex-1">{opcion.valor}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {pregunta.renderType === 'checkBox' && pregunta.opcionesRespuesta && (
                    <div className="space-y-3">
                      {pregunta.opcionesRespuesta.map((opcion) => {
                        const currentValue = respuestas[pregunta.preguntaId];
                        const isChecked = Array.isArray(currentValue) && currentValue.includes(opcion.id);
                        
                        return (
                          <label
                            key={opcion.id}
                            className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              isChecked
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleCheckbox(pregunta.preguntaId, opcion.id)}
                              className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                            />
                            <span className="text-gray-700 flex-1">{opcion.valor}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {pregunta.renderType === 'inputText' && (
                    <div>
                      <input
                        type="text"
                        value={(respuestas[pregunta.preguntaId] as string) || ''}
                        onChange={(e) => handleTextInput(pregunta.preguntaId, e.target.value)}
                        placeholder="Escribe tu respuesta aquí..."
                        maxLength={255}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                      />
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {((respuestas[pregunta.preguntaId] as string) || '').length}/255 caracteres
                      </div>
                    </div>
                  )}

                  {pregunta.renderType === 'inputTextArea' && (
                    <textarea
                      value={(respuestas[pregunta.preguntaId] as string) || ''}
                      onChange={(e) => handleTextInput(pregunta.preguntaId, e.target.value)}
                      placeholder="Escribe tu respuesta aquí..."
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors resize-vertical"
                    />
                  )}
                </div>
              ))}
          </div>

          {/* Botones */}
          <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
            <p className="text-sm text-gray-700">
              <span className="text-red-500 font-bold">*</span> Indica pregunta obligatoria
            </p>
          </div>
          
          <div className="flex gap-4">
            {!encuesta.obligatoria && (
              <Button
                variant="neutral"
                className="flex-1 py-4"
                size="lg"
                onClick={handleOmitir}
              >
                Omitir encuesta
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              variant="primary"
              className={`${encuesta.obligatoria ? 'w-full' : 'flex-1'} py-4`}
              size="lg"
              isLoading={isSubmitting}
              disabled={
                // Verificar que todas las preguntas OBLIGATORIAS estén respondidas
                encuesta.preguntas
                  .filter(p => p.esObligatoria)
                  .some(p => {
                    const respuesta = respuestas[p.preguntaId];
                    if (respuesta === undefined) return true;
                    if (typeof respuesta === 'string' && respuesta === '') return true;
                    if (Array.isArray(respuesta) && respuesta.length === 0) return true;
                    return false;
                  })
              }
            >
              {encuesta.obligatoria ? 'Completar encuesta' : 'Enviar respuestas'}
            </Button>
          </div>

          {encuesta.obligatoria && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Esta encuesta es obligatoria para continuar usando la plataforma
            </p>
          )}

          <div className="text-center mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Usuario: <span className="font-semibold">{session?.user.nombre}</span> • 
              Almacén: <span className="font-semibold">{session?.almacen.nombre}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
